import fetchWithAuth from "./secureFetch";
import { UserStory } from "../pages/interface/types";
const BACKEND_URI = "";
export interface CreateUserStoryRequest {
    project_id: number;
    title: string;
    user_story: string;
    ticket_summary?: string;
    description?: string;
    feature_id?: number;
    story_number?: string;
    external_id?: string;
    acceptance_criteria?: Record<string, any>;
    epic_id?: string;
    sprint_id?: string;
    story_type?: string;
    priority?: string;
    story_points?: number;
    business_value?: number;
    risk_level?: string;
    status?: string;
    assigned_to?: number;
    product_owner_id?: number;
    due_date?: string;
    estimated_hours?: number;
    tags?: string[];
    labels?: Record<string, any>;
    custom_fields?: Record<string, any>;
}
export interface UpdateUserStoryRequest extends Partial<CreateUserStoryRequest> {
    id?: string;
}
export interface UserStorySearchRequest {
    project_id?: number;
    feature_id?: number;
    status?: string;
    priority?: string;
    assigned_to?: number;
    search_term?: string;
    tags?: string[];
    summary?: boolean;
}
export interface UserStoryResponse {
    user_story: UserStory;
}
export interface UserStoriesResponse {
    user_stories: UserStory[];
}
export interface UserStoryStatisticsResponse {
    statistics: {
        total: number;
        by_status: Record<string, number>;
        by_priority: Record<string, number>;
        total_story_points: number;
        completed_story_points: number;
        average_story_points: number;
    };
}

export interface UserStoryJiraExportData {
    id: string;
    project_id: number;
    feature_id: number;
    user_story_title: string;
    project_title: string;
    feature_title: string;
    content: string;
    story_number: string;
    workproduct_id: number;
    workproduct_title: string;
    jira_id: string | null;
    jira_key: string | null;
    // Azure DevOps fields (also populated from external_id/ticket_summary)
    azure_devops_id: string | null;
    azure_devops_url: string | null;
    priority: string | null;
    story_points: number | null;
}

/**
 * Create a new user story
 */
export async function createUserStory(request: CreateUserStoryRequest, idToken: string): Promise<UserStory> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories`, idToken, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create user story");
    }
    const data: UserStoryResponse = await response.json();
    return data.user_story;
}
/**
 * Get a user story by ID
 */
export async function getUserStory(storyId: string, idToken: string): Promise<UserStory> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch user story");
    }
    const data: UserStoryResponse = await response.json();
    return data.user_story;
}

/**
 * Resolve a Context Mesh user-story reference across both story stores.
 *
 * The analyzer intentionally includes local and tracker-imported stories in
 * the same product snapshot. Feature Studio deep links therefore cannot
 * assume that every analyzer UUID belongs to the local user_stories table.
 */
export async function getFeatureStudioUserStory(storyId: string, idToken: string): Promise<UserStory> {
    try {
        return await getUserStory(storyId, idToken);
    } catch (localStoryError) {
        const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories-jira-import/${storyId}`, idToken, {
            method: "GET"
        });
        if (!response.ok) {
            throw localStoryError;
        }
        const data: UserStoryResponse = await response.json();
        return data.user_story;
    }
}
/**
 * Get a user story by story number
 */
export async function getUserStoryByNumber(storyNumber: string, idToken: string): Promise<UserStory> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/number/${storyNumber}`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch user story");
    }
    const data: UserStoryResponse = await response.json();
    return data.user_story;
}
/**
 * Get all user stories for a project
 */
export async function getUserStoriesByProject(
    projectId: number,
    idToken: string,
    includeArchived: boolean = false,
    summaryOnly: boolean = false
): Promise<UserStory[]> {
    const params = new URLSearchParams();
    if (includeArchived) params.append("include_archived", "true");
    if (summaryOnly) params.append("summary", "true");
    const url = `${BACKEND_URI}/api/projects/${projectId}/user-stories${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch project user stories");
    }
    const data: UserStoriesResponse = await response.json();
    return data.user_stories;
}
/**
 * Get all user stories for a feature
 */
export async function getUserStoriesByFeature(
    featureId: number,
    idToken: string,
    includeArchived: boolean = false,
    summaryOnly: boolean = false
): Promise<UserStory[]> {
    const params = new URLSearchParams();
    if (includeArchived) params.append("include_archived", "true");
    if (summaryOnly) params.append("summary", "true");
    const url = `${BACKEND_URI}/api/features/${featureId}/user-stories${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch feature user stories");
    }
    const data: UserStoriesResponse = await response.json();
    return data.user_stories;
}
/**
 * Get all user stories for a sprint
 */
export async function getUserStoriesBySprint(sprintId: string, idToken: string, summaryOnly: boolean = false): Promise<UserStory[]> {
    const params = new URLSearchParams();
    if (summaryOnly) params.append("summary", "true");
    const url = `${BACKEND_URI}/api/sprints/${sprintId}/user-stories${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch sprint user stories");
    }
    const data: UserStoriesResponse = await response.json();
    return data.user_stories;
}
/**
 * Update a user story
 */
export async function updateUserStory(storyId: string, updates: UpdateUserStoryRequest, idToken: string): Promise<UserStory> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}`, idToken, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update user story");
    }
    const data: UserStoryResponse = await response.json();
    return data.user_story;
}
/**
 * Update user story status
 */
export async function updateUserStoryStatus(storyId: string, status: string, idToken: string): Promise<UserStory> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}/status`, idToken, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ status })
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update user story status");
    }
    const data: UserStoryResponse = await response.json();
    return data.user_story;
}
/**
 * Assign a user story to a user
 */
export async function assignUserStory(storyId: string, userId: number, idToken: string): Promise<UserStory> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}/assign`, idToken, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ assigned_to: userId })
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to assign user story");
    }
    const data: UserStoryResponse = await response.json();
    return data.user_story;
}
/**
 * Delete a user story
 */
export async function deleteUserStory(storyId: string, idToken: string, softDelete: boolean = true): Promise<void> {
    const params = new URLSearchParams();
    params.append("soft", softDelete.toString());
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}?${params.toString()}`, idToken, {
        method: "DELETE"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete user story");
    }
}
/**
 * Archive a user story
 */
export async function archiveUserStory(storyId: string, idToken: string): Promise<void> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}/archive`, idToken, {
        method: "POST"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to archive user story");
    }
}
/**
 * Search user stories
 */
export async function searchUserStories(searchRequest: UserStorySearchRequest, idToken: string): Promise<UserStory[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/search`, idToken, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(searchRequest)
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to search user stories");
    }
    const data: UserStoriesResponse = await response.json();
    return data.user_stories;
}
/**
 * Get user story statistics for a project
 */
export async function getUserStoryStatistics(projectId: number, idToken: string): Promise<UserStoryStatisticsResponse["statistics"]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/projects/${projectId}/user-stories/statistics`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch user story statistics");
    }
    const data: UserStoryStatisticsResponse = await response.json();
    return data.statistics;
}
/**
 * Bulk create user stories
 */
export async function bulkCreateUserStories(userStories: CreateUserStoryRequest[], idToken: string): Promise<UserStory[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/bulk`, idToken, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ user_stories: userStories })
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to bulk create user stories");
    }
    const data: UserStoriesResponse = await response.json();
    return data.user_stories;
}

/**
 * Get user story data for Jira export
 */
export async function getUserStoryJiraExportData(storyId: string, sectionId: number, idToken: string): Promise<UserStoryJiraExportData> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/user-stories/${storyId}/jira-export-data?section_id=${sectionId}`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch user story Jira export data");
    }

    return await response.json();
}
